import React, { useCallback, useEffect, useRef, useState } from "react";
import ConfirmationModal from "../../../../components/ConfirmationModal";
import LivePriceModal from "../../../../components/LivePriceModal";
import PerformanceChartModal from "../../../../components/PerformanceChartModal";
import TradableTimesModal from "../../../../components/TradableTimesModal";
import { useTheme } from "../../../../contexts/ThemeContext";
import { useToast } from "../../../../contexts/ToastContext";
import { useContractPrices } from "../../../../hooks/useContractPrices";
import { useEssentialContracts } from "../../../../hooks/useEssentialContracts";
import { useInlineEditing } from "../../../../hooks/useInlineEditing";
import { CONTRACT_COLUMNS, Contract } from "../../../../types/contract";
import { buildCSV, triggerCSVDownload } from "../../../../utils/csvExport";
import ContractReferenceDataModal from "../../Contracts/components/ContractReferenceDataModal";
import ContractsTable from "../../Contracts/components/ContractsTable";
import ContractUsageModal from "../../Contracts/components/ContractUsageModal";
import StockOverrideModal from "../../overrides/StockOverrideModal";
import EssentialContractsToolbar from "./EssentialContractsToolbar";

const EssentialContractsTab: React.FC = () => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  const toast = useToast();

  const {
    contracts,
    allContracts,
    loading,
    loadingProgress,
    error,
    missingCount,
    missingIds,
    pagination,
    sorting,
    search,
    selection,
    actions: contractActions,
  } = useEssentialContracts();

  // Emit toast when hydration finds missing contracts (only once per fetch cycle)
  const prevMissingCount = useRef(0);
  useEffect(() => {
    if (missingCount > 0 && missingCount !== prevMissingCount.current) {
      toast.warning(
        `${missingCount} essential contract(s) could not be found -- click "View Missing IDs" for details`
      );
    }
    prevMissingCount.current = missingCount;
  }, [missingCount, toast]);

  const {
    livePrices,
    fetchingPrices,
    fetchingAllPrices,
    fetchPriceForContract,
    fetchAllPrices,
  } = useContractPrices();

  const {
    editingCell,
    pendingChanges,
    savingChanges,
    handleCellDoubleClick,
    handleCellChange,
    handleCellBlur,
    handleCellKeyDown,
    saveChanges,
    discardChanges,
  } = useInlineEditing(contractActions.fetchContracts);

  // Modal states
  const [tradableTimesModalOpen, setTradableTimesModalOpen] = useState(false);
  const [tradableTimesContract, setTradableTimesContract] = useState<Contract | null>(null);
  const [livePriceModalOpen, setLivePriceModalOpen] = useState(false);
  const [livePriceContract, setLivePriceContract] = useState<Contract | null>(null);
  const [toleranceModalOpen, setToleranceModalOpen] = useState(false);
  const [tolerancePercent, setTolerancePercent] = useState(2);
  const [verifyMatchesModalOpen, setVerifyMatchesModalOpen] = useState(false);
  const [matchingConIdsForVerify, setMatchingConIdsForVerify] = useState<number[]>([]);
  const [showChangesModal, setShowChangesModal] = useState(false);
  const [showMissingIdsModal, setShowMissingIdsModal] = useState(false);
  const [missingIdsCopied, setMissingIdsCopied] = useState(false);
  const [factorsContract, setFactorsContract] = useState<Contract | null>(null);
  const [overridesContract, setOverridesContract] = useState<Contract | null>(null);
  const [usageContract, setUsageContract] = useState<Contract | null>(null);
  const [referenceDataContract, setReferenceDataContract] = useState<Contract | null>(null);

  const openTradableTimes = (contract: Contract) => {
    setTradableTimesContract(contract);
    setTradableTimesModalOpen(true);
  };

  const openLivePrice = (contract: Contract) => {
    setLivePriceContract(contract);
    setLivePriceModalOpen(true);
  };

  const openToleranceModal = () => {
    setToleranceModalOpen(true);
  };

  const handleFindMatches = () => {
    const matchingConIds: number[] = [];

    contracts.forEach((contract) => {
      const priceData = livePrices[String(contract.conId)];
      if (!priceData) return;

      const ibkr = priceData.find((p) => p.source === "IBKR")?.price;
      const yahoo = priceData.find((p) => p.source === "YAHOO")?.price;
      const eodhd = priceData.find((p) => p.source === "EODHD")?.price;

      if (ibkr && yahoo && eodhd && ibkr > 0 && yahoo > 0 && eodhd > 0) {
        const avgPrice = (ibkr + yahoo + eodhd) / 3;
        const maxDelta = avgPrice * (tolerancePercent / 100);

        if (Math.abs(ibkr - yahoo) <= maxDelta && Math.abs(yahoo - eodhd) <= maxDelta) {
          matchingConIds.push(contract.conId);
        }
      }
    });

    setToleranceModalOpen(false);

    if (matchingConIds.length > 0) {
      selection.setSelectedConIds(new Set(matchingConIds));
      setMatchingConIdsForVerify(matchingConIds);
      setVerifyMatchesModalOpen(true);
    } else {
      toast.info(`No price matches found within ${tolerancePercent}% tolerance.`);
    }
  };

  const handleConfirmVerifyMatches = () => {
    contractActions.verifyContracts(matchingConIdsForVerify);
    setVerifyMatchesModalOpen(false);
    setMatchingConIdsForVerify([]);
  };

  const hasPendingChanges = pendingChanges.size > 0;

  // CSV Download
  const [downloadingCSV, setDownloadingCSV] = useState(false);

  const handleDownloadCSV = useCallback(() => {
    try {
      setDownloadingCSV(true);

      const headers = [
        "conId",
        "symbol",
        "secType",
        "currency",
        "exchange",
        "primaryExchange",
        "longName",
        "isin",
        "validExchanges",
        "optionAllowed",
        "optionExchange",
        "optionType",
        "eodhdTicker",
        "yahooTickerName",
        "verifiedAtUtc",
        "updatedAt",
      ];

      const rows = allContracts.map((c) =>
        headers.map((h) => {
          const val = c[h as keyof Contract];
          if (val === null || val === undefined) return "";
          return String(val);
        })
      );

      const csv = buildCSV(headers, rows);
      const filename = `essential_contracts_${new Date().toISOString().split("T")[0]}.csv`;
      triggerCSVDownload(csv, filename);
    } finally {
      setDownloadingCSV(false);
    }
  }, [allContracts]);

  if (loading && contracts.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div
            className={`animate-spin rounded-full h-8 w-8 border-b-2 ${
              isDarkMode ? "border-rp-amber" : "border-blue-600"
            } mx-auto`}
          />
          <p className={`mt-3 text-sm ${isDarkMode ? "text-rp-grey/50" : "text-gray-600"}`}>
            {loadingProgress || "Loading essential contracts..."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <EssentialContractsToolbar
        searchQuery={search.searchQuery}
        onSearchChange={search.setSearchQuery}
        selectionSize={selection.selectedConIds.size}
        loading={loading}
        bulkActionLoading={contractActions.bulkActionLoading}
        fetchingAllPrices={fetchingAllPrices}
        hasContracts={contracts.length > 0}
        onRefresh={contractActions.fetchContracts}
        onFetchAllPrices={() =>
          fetchAllPrices(
            contracts.filter((c) => c.exchange !== "VALUE").map((c) => c.conId)
          )
        }
        onVerifySelected={() => contractActions.verifyContracts()}
        onUnverifySelected={() => contractActions.unverifyContracts()}
        onVerifyMatches={openToleranceModal}
        onDownloadCSV={handleDownloadCSV}
        downloading={downloadingCSV}
      />

      {error && (
        <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
          <span className="block sm:inline">{error}</span>
        </div>
      )}

      {missingCount > 0 && (
        <div className="mb-4 bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded relative text-sm">
          <div className="flex items-center justify-between">
            <span>
              {missingCount} essential contract(s) could not be found in the contracts database.
            </span>
            <button
              onClick={() => {
                setShowMissingIdsModal(true);
                setMissingIdsCopied(false);
              }}
              className="ml-2 px-3 py-1 text-xs font-medium rounded border border-yellow-500 hover:bg-yellow-200 transition-colors whitespace-nowrap"
            >
              View Missing IDs
            </button>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center mb-2">
        <div className={`text-sm ${isDarkMode ? "text-rp-grey/50" : "text-gray-600"}`}>
          {pagination.totalItems} essential contracts
        </div>
        {hasPendingChanges && (
          <div className="flex gap-2">
            <button
              onClick={discardChanges}
              className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 dark:text-rp-grey/50 dark:hover:text-rp-grey"
            >
              Discard Changes
            </button>
            <button
              onClick={() => setShowChangesModal(true)}
              className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 shadow-sm"
            >
              Review {pendingChanges.size} Changes
            </button>
          </div>
        )}
      </div>

      <ContractsTable
        contracts={contracts}
        columns={CONTRACT_COLUMNS}
        loading={loading}
        selectedConIds={selection.selectedConIds}
        sortColumn={sorting.sortColumn}
        sortDirection={sorting.sortDirection}
        onSort={sorting.handleSort}
        onSelectAll={selection.handleSelectAll}
        onSelectOne={selection.handleSelectOne}
        editingCell={editingCell}
        editValue={editingCell?.value || ""}
        pendingChanges={pendingChanges}
        onCellDoubleClick={handleCellDoubleClick}
        onCellChange={handleCellChange}
        onCellBlur={() => handleCellBlur(contracts)}
        onCellKeyDown={(e) => handleCellKeyDown(e, contracts)}
        livePrices={livePrices}
        fetchingPrices={fetchingPrices}
        onFetchPrice={fetchPriceForContract}
        onOpenTradableTimes={openTradableTimes}
        onOpenLivePrice={openLivePrice}
        onOpenFactors={(contract) => setFactorsContract(contract)}
        onOpenOverrides={(contract) => setOverridesContract(contract)}
        onOpenUsage={(contract) => setUsageContract(contract)}
        onOpenReferenceData={(contract) => setReferenceDataContract(contract)}
      />

      {/* Pagination Footer */}
      <div className={`mt-4 flex items-center justify-between ${isDarkMode ? "text-rp-grey/50" : "text-gray-600"}`}>
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-2">
            <span>Show</span>
            <select
              value={pagination.pageSize}
              onChange={(e) => pagination.setPageSize(Number(e.target.value))}
              className="border rounded p-1 dark:bg-rp-surface dark:border-rp-separator"
            >
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={500}>500</option>
              <option value={1000}>1000</option>
              <option value={pagination.totalItems || 99999}>All</option>
            </select>
          </div>
          <span>
            Showing{" "}
            {pagination.totalItems === 0
              ? 0
              : (pagination.currentPage - 1) * pagination.pageSize + 1}{" "}
            to {Math.min(pagination.currentPage * pagination.pageSize, pagination.totalItems)} of{" "}
            {pagination.totalItems}
          </span>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => pagination.setCurrentPage(1)}
            disabled={pagination.currentPage === 1}
            className={`px-2 py-1 text-sm rounded disabled:opacity-50 ${
              isDarkMode ? "hover:bg-rp-surface-raised" : "hover:bg-gray-100"
            }`}
          >
            First
          </button>
          <button
            onClick={() => pagination.setCurrentPage(Math.max(1, pagination.currentPage - 1))}
            disabled={pagination.currentPage === 1}
            className={`px-2 py-1 text-sm rounded disabled:opacity-50 ${
              isDarkMode ? "hover:bg-rp-surface-raised" : "hover:bg-gray-100"
            }`}
          >
            Prev
          </button>
          <span className="px-2 py-1 text-sm">
            Page {pagination.currentPage} of {pagination.totalPages}
          </span>
          <button
            onClick={() =>
              pagination.setCurrentPage(Math.min(pagination.totalPages, pagination.currentPage + 1))
            }
            disabled={pagination.currentPage === pagination.totalPages}
            className={`px-2 py-1 text-sm rounded disabled:opacity-50 ${
              isDarkMode ? "hover:bg-rp-surface-raised" : "hover:bg-gray-100"
            }`}
          >
            Next
          </button>
        </div>
      </div>

      {/* Tolerance Modal */}
      {toleranceModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div
            className={`${isDarkMode ? "bg-rp-surface" : "bg-white"} rounded-lg shadow-xl max-w-sm w-full overflow-hidden`}
          >
            <div
              className={`flex items-center justify-between p-6 border-b ${
                isDarkMode ? "border-rp-separator" : "border-gray-200"
              }`}
            >
              <h2 className={`text-lg font-semibold ${isDarkMode ? "text-rp-grey" : "text-gray-900"}`}>
                Verify Price Matches
              </h2>
              <button
                onClick={() => setToleranceModalOpen(false)}
                className={`transition-colors ${
                  isDarkMode ? "text-rp-grey/50 hover:text-rp-grey/70" : "text-gray-400 hover:text-gray-600"
                }`}
              >
                <span className="sr-only">Close</span>
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              <p className={`text-sm mb-4 ${isDarkMode ? "text-rp-grey/70" : "text-gray-600"}`}>
                Find contracts where IBKR, Yahoo, and EODHD prices match within the specified tolerance.
              </p>
              <label
                className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-rp-grey/70" : "text-gray-700"}`}
              >
                Price Tolerance (%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={tolerancePercent}
                onChange={(e) => setTolerancePercent(Math.max(0, Math.min(100, Number(e.target.value))))}
                className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 ${
                  isDarkMode
                    ? "focus:ring-rp-amber bg-rp-surface border-rp-separator text-rp-grey"
                    : "focus:ring-blue-500 bg-white border-gray-300 text-gray-900"
                }`}
              />
            </div>
            <div
              className={`flex items-center justify-end p-6 border-t space-x-3 ${
                isDarkMode ? "border-rp-separator" : "border-gray-200"
              }`}
            >
              <button
                type="button"
                onClick={() => setToleranceModalOpen(false)}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  isDarkMode
                    ? "text-rp-grey/70 hover:text-rp-grey hover:bg-rp-surface-raised"
                    : "text-gray-600 hover:text-gray-700 hover:bg-gray-50"
                }`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleFindMatches}
                className="px-4 py-2 text-white rounded-lg transition-colors bg-blue-600 hover:bg-blue-700"
              >
                Find Matches
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save Changes Modal */}
      <ConfirmationModal
        isOpen={showChangesModal}
        onClose={() => setShowChangesModal(false)}
        onConfirm={() => saveChanges(contracts)}
        title="Save Changes"
        message={
          (
            <div className="max-h-96 overflow-y-auto">
              <p className="mb-4">You are about to save changes to {pendingChanges.size} contracts:</p>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-gray-200 dark:border-rp-separator">
                    <th className="py-2">ConID</th>
                    <th className="py-2">Field</th>
                    <th className="py-2">Old Value</th>
                    <th className="py-2">New Value</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.from(pendingChanges.entries()).map(([conId, fields]) =>
                    Object.entries(fields).map(([key, { oldValue, newValue }]) => (
                      <tr key={`${conId}-${key}`} className="border-b border-gray-100 dark:border-rp-separator">
                        <td className="py-2 font-mono">{conId}</td>
                        <td className="py-2">{key}</td>
                        <td className="py-2 text-red-500 line-through">{oldValue || "(empty)"}</td>
                        <td className="py-2 text-green-500">{newValue}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          ) as any
        }
        confirmText={savingChanges ? "Saving..." : "Save Changes"}
        loading={savingChanges}
      />

      {/* Verify Matches Modal */}
      <ConfirmationModal
        isOpen={verifyMatchesModalOpen}
        onClose={() => {
          setVerifyMatchesModalOpen(false);
          setMatchingConIdsForVerify([]);
        }}
        onConfirm={handleConfirmVerifyMatches}
        title="Verify Matching Contracts"
        message={`Found ${matchingConIdsForVerify.length} contracts with prices matching within ${tolerancePercent}% tolerance across IBKR, Yahoo, and EODHD. Verify them now?`}
        confirmText="Verify"
      />

      {/* Tradable Times Modal */}
      {tradableTimesContract && (
        <TradableTimesModal
          isOpen={tradableTimesModalOpen}
          onClose={() => {
            setTradableTimesModalOpen(false);
            setTradableTimesContract(null);
          }}
          tradableTimesUtc={tradableTimesContract.tradableTimesUtc || null}
          symbol={tradableTimesContract.symbol}
          timeZoneId={tradableTimesContract.timeZoneId || null}
        />
      )}

      {/* Live Price Modal */}
      {livePriceContract && (
        <LivePriceModal
          isOpen={livePriceModalOpen}
          onClose={() => {
            setLivePriceModalOpen(false);
            setLivePriceContract(null);
          }}
          contract={livePriceContract}
          onContractUpdated={contractActions.fetchContracts}
        />
      )}

      {/* Stock Overrides Modal */}
      {overridesContract && (
        <StockOverrideModal
          isOpen={true}
          onClose={() => setOverridesContract(null)}
          conId={overridesContract.conId}
          symbol={overridesContract.symbol}
        />
      )}

      {/* Contract Factors Modal */}
      {factorsContract && (
        <PerformanceChartModal
          isOpen={true}
          onClose={() => setFactorsContract(null)}
          entityId={factorsContract.conId}
          entityType="CONTRACT"
          entityName={factorsContract.symbol}
          dataSource="v3-factors"
          entityCreatedAt={factorsContract.createdAt}
        />
      )}

      {/* Contract Usage Modal */}
      {usageContract && (
        <ContractUsageModal
          isOpen={true}
          onClose={() => setUsageContract(null)}
          conId={usageContract.conId}
          symbol={usageContract.symbol}
        />
      )}

      {/* Reference Data Modal */}
      {referenceDataContract && (
        <ContractReferenceDataModal
          isOpen={true}
          onClose={() => setReferenceDataContract(null)}
          contract={referenceDataContract}
          onSaved={contractActions.fetchContracts}
        />
      )}

      {/* Missing Contract IDs Modal */}
      {showMissingIdsModal && missingIds.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div
            className={`${isDarkMode ? "bg-rp-surface" : "bg-white"} rounded-lg shadow-xl max-w-lg w-full overflow-hidden`}
          >
            <div
              className={`flex items-center justify-between p-6 border-b ${
                isDarkMode ? "border-rp-separator" : "border-gray-200"
              }`}
            >
              <h2 className={`text-lg font-semibold ${isDarkMode ? "text-rp-grey" : "text-gray-900"}`}>
                Missing Contract IDs ({missingIds.length})
              </h2>
              <button
                onClick={() => setShowMissingIdsModal(false)}
                className={`transition-colors ${
                  isDarkMode ? "text-rp-grey/50 hover:text-rp-grey/70" : "text-gray-400 hover:text-gray-600"
                }`}
              >
                <span className="sr-only">Close</span>
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              <p className={`text-sm mb-3 ${isDarkMode ? "text-rp-grey/50" : "text-gray-600"}`}>
                These contract IDs exist in the essential contracts list but were not found in the contracts database.
              </p>
              <div
                className={`max-h-64 overflow-y-auto border rounded-md p-3 font-mono text-sm ${
                  isDarkMode
                    ? "bg-rp-black border-rp-separator text-rp-grey/70"
                    : "bg-gray-50 border-gray-200 text-gray-800"
                }`}
              >
                {missingIds.map((id, idx) => (
                  <div
                    key={id}
                    className={`py-1 px-2 ${
                      idx % 2 === 0
                        ? isDarkMode ? "bg-rp-surface/50" : "bg-gray-100/50"
                        : ""
                    }`}
                  >
                    {id}
                  </div>
                ))}
              </div>
            </div>
            <div
              className={`flex items-center justify-end gap-3 p-6 border-t ${
                isDarkMode ? "border-rp-separator" : "border-gray-200"
              }`}
            >
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(missingIds.join("\n"));
                  setMissingIdsCopied(true);
                  setTimeout(() => setMissingIdsCopied(false), 2000);
                }}
                className={`px-4 py-2 text-sm rounded-lg transition-colors border ${
                  missingIdsCopied
                    ? "bg-green-100 border-green-400 text-green-700"
                    : isDarkMode
                      ? "border-rp-separator text-rp-grey/70 hover:bg-rp-surface-raised"
                      : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                {missingIdsCopied ? "Copied" : "Copy All IDs"}
              </button>
              <button
                type="button"
                onClick={() => setShowMissingIdsModal(false)}
                className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                  isDarkMode
                    ? "text-rp-grey/70 hover:text-rp-grey hover:bg-rp-surface-raised"
                    : "text-gray-600 hover:text-gray-700 hover:bg-gray-50"
                }`}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EssentialContractsTab;
